
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, AlertCircle, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getLocalizedAuthError } from "@/utils/authErrorMapping";

const UpdatePasswordPage = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [userEmail, setUserEmail] = useState("");
  const [sessionReady, setSessionReady] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    
    const initializeSession = async () => {
      // Check for recovery tokens in URL
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const type = searchParams.get('type');
      

      // If we have tokens in URL, set the session
      if (accessToken && refreshToken && type === 'recovery') {
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          
          if (error) {
            console.error("Error setting session", { name: (error as any)?.name, status: (error as any)?.status });
            toast.error("Link inválido ou expirado", {
              description: "Por favor, solicite um novo link de redefinição de senha."
            });
            navigate('/esqueci-senha');
            return;
          }
          
          if (data.session?.user?.email) {
            setUserEmail(data.session.user.email);
            setSessionReady(true);
            toast.success("Link válido", {
              description: "Digite sua nova senha abaixo."
            });
          }
        } catch (error) {
          console.error("Exception setting session", { message: error instanceof Error ? error.message : 'unknown' });
          toast.error("Erro ao processar link", {
            description: "Tente novamente ou solicite um novo link."
          });
          navigate('/esqueci-senha');
        }
      } else {
        // Check if user already has a valid session
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          toast.error("Link inválido ou expirado", {
            description: "Por favor, solicite um novo link de redefinição de senha."
          });
          navigate('/esqueci-senha');
          return;
        }
        
        // If we have a session but no recovery type, it might be an invalid access
        if (type !== 'recovery') {
          toast.error("Acesso inválido", {
            description: "Use o link enviado por email para redefinir sua senha."
          });
          navigate('/esqueci-senha');
          return;
        }
        
        setUserEmail(session.user?.email || "");
        setSessionReady(true);
      }
    };

    initializeSession();
  }, [searchParams, navigate]);

  // Real-time validation
  useEffect(() => {
    const errors: string[] = [];
    
    if (password && password.length < 6) {
      errors.push("A senha deve ter pelo menos 6 caracteres.");
    }
    
    if (password && confirmPassword && password !== confirmPassword) {
      errors.push("As senhas informadas não coincidem.");
    }
    
    setValidationErrors(errors);
  }, [password, confirmPassword]);

  const handleSkip = () => {
    toast.info("Redefinição de senha cancelada");
    navigate('/login');
  };

  const handleSavePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    
    // Final validation
    if (!password.trim()) {
      toast.error("Por favor, informe a nova senha");
      return;
    }

    if (password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    if (!confirmPassword.trim()) {
      toast.error("Por favor, confirme a nova senha");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }

    setLoading(true);
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      });
      
      
      if (error) {
        console.error("Error updating password", { name: (error as any)?.name, status: (error as any)?.status });
        const localizedError = await getLocalizedAuthError(error.message);
        toast.error("Erro ao atualizar senha", {
          description: localizedError
        });
      } else {
        toast.success("Senha redefinida com sucesso!", {
          description: "Redirecionando para o login..."
        });
        
        // Sign out to ensure user logs in with new password
        await supabase.auth.signOut();
        
        // Check if we should return to simulation
        const returnTo = searchParams.get('returnTo');
        const simulationId = searchParams.get('simulationId');
        
        // Wait a moment and redirect appropriately
        setTimeout(() => {
          if (returnTo === 'infos-festa' && simulationId) {
            navigate(`/infos-festa?simulationId=${simulationId}`);
          } else {
            navigate('/login');
          }
        }, 2000);
      }
    } catch (error: any) {
      console.error("Exception updating password", { message: error instanceof Error ? error.message : 'unknown' });
      toast.error("Ocorreu um erro", {
        description: "Tente novamente mais tarde."
      });
    }
    
    setLoading(false);
  };

  // Show loading state while session is being established
  if (!sessionReady) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow py-16 bg-gradient-to-b from-accent to-white">
          <div className="container-custom">
            <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-muted-foreground">Validando link...</span>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow py-16 bg-gradient-to-b from-accent to-white">
        <div className="container-custom">
          <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8">
            {/* Título principal */}
            <h1 className="text-2xl font-bold text-center text-foreground mb-6">
              Redefinir senha
            </h1>
            
            {/* E-mail do usuário */}
            {userEmail && (
              <div className="mb-6 p-3 bg-accent rounded-lg flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">{userEmail}</span>
              </div>
            )}

            {/* Área de alerta de validação */}
            {validationErrors.length > 0 && (
              <Alert className="mb-6 border-pink-200 bg-pink-50">
                <AlertCircle className="h-4 w-4 text-pink-600" />
                <AlertDescription className="text-pink-800">
                  <ul className="list-disc list-inside">
                    {validationErrors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Formulário */}
            <form onSubmit={handleSavePassword} className="space-y-6">
              {/* Campo Nova senha */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-foreground font-medium">
                  Criar uma senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                  <Input 
                    id="password" 
                    type={showPasswords ? "text" : "password"}
                    placeholder="********" 
                    required 
                    className="pl-10 pr-10 focus:border-primary transition-colors"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                  >
                    {showPasswords ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Campo Confirmar senha */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-foreground font-medium">
                  Confirmar
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-muted-foreground" />
                  <Input 
                    id="confirmPassword" 
                    type={showPasswords ? "text" : "password"}
                    placeholder="********" 
                    required 
                    className="pl-10 pr-10 focus:border-primary transition-colors"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
                  >
                    {showPasswords ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Checkbox Mostrar senha */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="showPassword" 
                  checked={showPasswords}
                  onCheckedChange={(checked) => setShowPasswords(checked as boolean)}
                />
                <Label htmlFor="showPassword" className="text-sm text-muted-foreground">
                  Mostrar senha
                </Label>
              </div>

              {/* Botões */}
              <div className="flex gap-4 pt-4">
                {/* Botão Pular - à esquerda */}
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={handleSkip}
                  className="flex-1"
                  disabled={loading}
                >
                  Pular
                </Button>

                {/* Botão Salvar senha - à direita com cor laranja */}
                <Button 
                  type="submit" 
                  disabled={loading || validationErrors.length > 0}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Salvando...
                    </span>
                  ) : (
                    "Salvar senha"
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default UpdatePasswordPage;
